import { useState, useRef, useEffect } from 'react';
import { useFloating, offset, flip, shift, size } from '@floating-ui/react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import './Select.css';

export default function Select({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  disabled = false,
  className = '',
  getOptionLabel = (option) => option.label || option.name || String(option),
  getOptionValue = (option) => option.value || option.id || option,
  getOptionDescription = (option) => option.description || null,
  renderOption = null,
  renderTrigger = null, // (selectedOption, isOpen, disabled) => ReactNode
  specialActions = [],
  showDescriptions = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const selectedOption = options.find(opt => getOptionValue(opt) === value);
  const selectedLabel = selectedOption ? getOptionLabel(selectedOption) : placeholder;

  const handleSelect = (option) => {
    const optionValue = getOptionValue(option);
    onChange({ target: { value: optionValue } });
    setIsOpen(false);
  };

  return (
    <>
      {renderTrigger ? (
        <div
          ref={refs.setReference}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          {renderTrigger(selectedOption, isOpen, disabled)}
        </div>
      ) : (
        <button
          ref={refs.setReference}
          className={`select-button ${className} ${isOpen ? 'open' : ''}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          type="button"
          data-sfx="button"
        >
          <span className="select-value">{selectedLabel}</span>
          <ChevronDown size={16} className={`select-icon ${isOpen ? 'rotate' : ''}`} />
        </button>
      )}

      {isOpen && createPortal(
        <>
          <div
            className="select-backdrop"
            onClick={() => setIsOpen(false)}
          />
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="select-dropdown"
          >
            {options.map((option, index) => {
              const optionValue = getOptionValue(option);
              const optionDescription = getOptionDescription(option);
              const isSelected = optionValue === value;

              return (
                <button
                  key={optionValue}
                  onClick={() => handleSelect(option)}
                  className={`select-option ${isSelected ? 'selected' : ''} ${showDescriptions ? 'with-description' : ''}`}
                  type="button"
                  data-sfx="button"
                >
                  {renderOption ? renderOption(option, isSelected) : (
                    <>
                      <div className="select-option-content">
                        <span className="select-option-label">{getOptionLabel(option)}</span>
                        {showDescriptions && optionDescription && (
                          <span className="select-option-description">{optionDescription}</span>
                        )}
                      </div>
                      {isSelected && <span className="check-icon">✓</span>}
                    </>
                  )}
                </button>
              );
            })}

            {/* Special actions section */}
            {specialActions.length > 0 && (
              <div className="select-special-actions">
                {specialActions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick();
                      setIsOpen(false);
                    }}
                    className={`select-option select-special-action ${action.className || ''}`}
                    disabled={action.disabled}
                    type="button"
                    title={action.disabled ? action.tooltip : ''}
                    data-sfx="button"
                  >
                    <span>{action.label}</span>
                    {action.disabled && action.tooltip && (
                      <span className="action-hint" title={action.tooltip}>ⓘ</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </>
  );
}
